import { observable, action, computed, runInAction } from "mobx";
import { DateTime } from "luxon";

import {
	PrematchOfferStore,
	BettingTypeSelectorsStore as BaseBettingTypeSelectorsStore,
	EventOffer,
	SportOffer,
	getEventSortByFavorite,
	getEventSortBySportCategory,
	getEventSortByTournament,
} from "@gp/offer";
import { Sorter } from "@gp/utility";
import { LiveStatus, EventType } from "@gp/models";
import { TOfferRequest, TOfferSortType } from "@gp/hub";

import { IStoreLifeCycle } from "@interface/IStoreLifeCycle";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import betRules from "@betting-rules";
import { getCurrentCulture } from "@utils";
import { roundToStartOfNextHour } from "@state/common/PeriodConverter";
import { LoaderStore } from "@state/stores/common";

import {
	HomePageStoreConstructorsParameters,
	ISportSliderHeaderStore,
	StoreTypeEnum,
} from "./ISportSliderHeaderStore";
import FavoriteStore from "@state/stores/offer/shared/FavoritesStore";
import { RootStore } from "@state/stores/types";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const PrematchOfferApiService = lazy(loadFailPath,()=>import("@api-services/offer/PrematchOfferApiService"));

class BettingTypeSelectorsStore extends BaseBettingTypeSelectorsStore {
	@action.bound
	reset() {}

	@action.bound
	customReset() {
		super.reset();
	}
}

export class LastMinuteViewStore
	extends PrematchOfferStore
	implements ISportSliderHeaderStore, IStoreLifeCycle
{
	type = StoreTypeEnum.LastMinuteViewStore;

	private loader: LoaderStore;
	public rootStore: RootStore;
	private homeOfferLoader: LoaderStore;
	private favoriteStore: FavoriteStore;
	public route = "last-minute";

	//#region observable
	@observable public isFetchingData = false;
	@observable public isStoreInitialized = false;
	@observable public selectedSportId: string;
	@observable public sportsList: ISportSliderHeaderStore["sportsList"] = [];
	@observable public isSelectedSportOutOfOffer = false;

	//#endregion observable

	//#region  computed
	@computed public get shouldDisplayInitialLoader() {
		return !this.isStoreInitialized;
	}

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get isLoadingProcess() {
		return this.loader.isLoadingProcess;
	}

	@computed get isEmpty() {
		return (
			this.sportsList.length === 0 &&
			this.eventsList.length == 0 &&
			!this.isFetchingData &&
			this.isStoreInitialized
		);
	}

	@computed get mappedEventsList() {
		return (this.events as (EventOffer & { sport: SportOffer })[])
			.filter((e) => e.sportId === this.selectedSportId)
			.map((e) => {
				e.sport = this.getMappedSport(e);
				return e;
			});
	}

	@computed get eventsList(): (EventOffer & { sport: SportOffer })[] {
		if (
			this.favoriteStore &&
			this.favoriteStore.userFavoriteEventsSet.size > 0
		) {
			return (this.events as (EventOffer & { sport: SportOffer })[])
				.filter((e) => e.sportId === this.selectedSportId)
				.map((e) => {
					e.sport = this.getMappedSport(e);
					return e;
				})
				.sort(
					Sorter.sort((a, b) => {
						return Sorter.sort(
							(a1: EventOffer, b1: EventOffer) =>
								getEventSortByFavorite(
									a1,
									b1,
									this.favoriteStore
								),
							"startTime",
							(a: EventOffer, b: EventOffer) =>
								getEventSortBySportCategory(
									a,
									b,
									this.lookupsStore
								),
							(a: EventOffer, b: EventOffer) =>
								getEventSortByTournament(
									a,
									b,
									this.lookupsStore
								),
							"id"
						)(a, b);
					})
				);
		} else {
			return (this.events as (EventOffer & { sport: SportOffer })[])
				.filter((e) => e.sportId === this.selectedSportId)
				.map((e) => {
					e.sport = this.getMappedSport(e);
					return e;
				})
				.sort(
					Sorter.sort((a, b) => {
						return Sorter.sort("startTime", "id")(a, b);
					})
				);
		}
	}

	@computed get eventIdsForEventSwitcher() {
		const list: string[] = this.eventsList.map(({ id }) => id);

		return list;
	}

	//#endregion computed

	constructor(config: HomePageStoreConstructorsParameters) {
		// @ts-expect-error
		super({
			customConfiguration: betRules,
			bettingTypeSelectorsStore: new BettingTypeSelectorsStore(),
		});
		this.favoriteStore = config.favoritesStore;
		this.rootStore = config.rootStore;
		this.homeOfferLoader = config.loaderStore;

		this.loader = new LoaderStore();
	}

	//#region fetching data

	@action.bound
	public async onInitialize(sportAbrv?: string): Promise<void> {
		if (!this.isStoreInitialized) {
			await this.fetchSportsList(sportAbrv);
		}

		if (
			!this.sportsList.some((sp) => sp.id === this.selectedSportId) &&
			this.sportsList.length > 0
		) {
			await this.setSelectedSportId(this.sportsList[0].id);
		}
	}

	@action.bound
	private async fetchSportsList(sportAbrv?: string) {
		try {
			this.homeOfferLoader.suspend();

			const menuResponse = await (
				await PrematchOfferApiService
			).default.getPrematchMenu({
				Filter: {
					eventType: EventType.NORMAL,
					liveStatus: LiveStatus.PREMATCH,
					startTime: {
						lt: roundToStartOfNextHour(
							DateTime.utc().plus({ hours: 4 })
						).toJSDate(),
					},
				},
			});
			runInAction(() => {
				this.sportsList = menuResponse.map((sp) => ({
					...sp.sport,
					count: sp.count > 15 ? 15 : sp.count,
				}));

				if (
					this.sportsList.length > 0 &&
					(this.selectedSportId == null ||
						!this.sportsList.some(
							(sp) => sp.id === this.selectedSportId
						))
				) {
					if (sportAbrv) {
						const sport = this.sportsList.find(
							(sport) => sport.abrv === sportAbrv
						);
						if (sport) {
							this.setSelectedSportId(sport.id);
						}
					} else {
						if (this.selectedSportId != null) {
							this.isSelectedSportOutOfOffer = true;
						}
						this.setSelectedSportId(this.sportsList[0].id);
					}
				} else {
					this.onDoneFetching();
					this.homeOfferLoader.resume();
					if (this.sportsList.length > 0) {
						this.setSelectedSportId(this.sportsList[0].id);
					}
				}
			});
		} catch (e) {
			console.error("no menu response");
			this.onDoneFetching();
			this.homeOfferLoader.resume();
		}
	}

	@action.bound
	private async fetchData(): Promise<void> {
		this.loader.suspend();
		this.isFetchingData = true;

		const requestObj = this.generateOfferRequestObj();

		try {
			const offer = await (
				await PrematchOfferApiService
			).default.getOfferData(requestObj);

			this.assignOfferData(offer);
			if (this.eventsMap.size === 0) {
				await this.fetchSportsList();
				runInAction(() => {
					this.isSelectedSportOutOfOffer = true;
				});
				return;
			}
		} catch (e) {
			console.error(e);
		}

		this.homeOfferLoader.resume();
		this.onDoneFetching();
	}

	//#region request object

	private generateOfferRequestObj(): TOfferRequest {
		const filter = this.generateFilter();
		const sorting: TOfferSortType[] = [
			"startTime",
			"sportCategory",
			"sportCategoryId",
			"tournament",
		];
		return {
			paging: {
				pageNumber: 1,
				pageSize: 15,
			},
			sorting,
			filter,
		};
	}

	private generateFilter(): TOfferRequest["filter"] {
		const bettingTypeConfiguration = this.OfferMapper.getBettingTypes(
			"prematch",
			betRules
		);

		const filter: TOfferRequest["filter"] = {
			startTime: {
				lt: roundToStartOfNextHour(
					DateTime.utc().plus({ hours: 4 })
				).toJSDate(),
			},
			eventType: EventType.NORMAL,
			liveStatus: LiveStatus.PREMATCH,
			sportId: this.selectedSportId,
			offers: [
				{
					bettingType: {
						abrv: {
							eq: bettingTypeConfiguration.normal,
						},
					},
				},
				{
					bettingType: {
						abrv: {
							eq: bettingTypeConfiguration.marginal,
						},
					},
					isFavorite: true,
				},
			],
		};

		return filter;
	}

	//#endregion request object

	@action.bound
	private onDoneFetching(): void {
		this.isStoreInitialized = true;
		this.isFetchingData = false;
		this.loader.resume();
	}

	//#endregion fetching data

	//#region  disposers

	@action.bound
	public onDispose(): void {
		this.isSelectedSportOutOfOffer = false;
		this.isStoreInitialized = false;
		this.isFetchingData = false;
		this.loader.suspend();

		// @ts-expect-error
		this.configuration.bettingTypeSelectorsStore.customReset();
		this.reset();
	}

	//#endregion disposers

	//#region actions

	@action.bound
	public resetSelectedSportOutOfOffer() {
		this.setSelectedSportId(this.sportsList[0].id);
		this.isSelectedSportOutOfOffer = false;
	}

	@action.bound
	public async setSelectedSportId(newSport: string) {
		this.selectedSportId = newSport;
		await this.fetchData();
	}

	//#endregion actions
}
